/**
 * Mascaramento da sessão na estação + diálogo de reconfirmação (PBI-07).
 *
 * O item que mais costuma sair errado, então as regras ficam aqui em cima:
 *
 * - o diálogo **não fecha**. Não há "X", não há Esc, não há clique fora, não
 *   há "continuar" sozinho. Sai-se dele provando a identidade ou dizendo
 *   "sou outra pessoa". Um "sim" bastando invalidaria o PBI inteiro;
 * - "Sou outra pessoa" tem o mesmo tamanho do "Continuar como". Link pequeno
 *   no canto faria o próximo médico seguir na sessão do anterior;
 * - a superfície é opaca nos dois temas. O que esconde bem no claro pode
 *   deixar o prontuário legível no escuro, e aqui vazar é o pior caso;
 * - o resto da página fica `inert` enquanto a máscara está de pé: sem isso,
 *   Tab chegaria nos botões do prontuário por baixo dela.
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';
import { IDENTITY_DEMO_ENABLED, firstNameOf, useIdentitySession } from '../../context/IdentitySessionContext';
import DemoFaceControls from './DemoFaceControls';
import useFaceCapture, { CAPTURE_ERROR, CAPTURE_STARTING } from '../../hooks/useFaceCapture';
import {
  identityErrorCode,
  remainingAttempts,
  reverifyFaceFailureCode,
} from '../../services/identityService';

const PIN_PATTERN = /^\d{4,8}$/;

// Como no login: 3 falhas de rosto e só resta o PIN. Confirmar a identidade,
// por rosto ou PIN, zera a contagem.
const MAX_FACE_ATTEMPTS = 3;
// Falhas que contam. Rosto de outra pessoa não conta: não é o dono da sessão
// errando, é outra pessoa na frente da câmera.
const COUNTED_FACE_FAILURES = new Set(['not_recognized', 'not_enrolled', 'recognizer_unavailable']);

// Recusa do rosto -> texto. `different_person` muda a SAÍDA, não a tentativa:
// quem chegou não é o dono da sessão, e a tela oferece "Sou outra pessoa".
const FACE_FAILURE_KEY = {
  capture_failed: 'identity.mask.face.captureFailed',
  not_recognized: 'identity.mask.face.notRecognized',
  different_person: 'identity.mask.face.differentPerson',
  not_enrolled: 'identity.mask.face.notEnrolled',
  session_gone: 'identity.mask.sessionGone',
};

function useInertBackground(active) {
  useEffect(() => {
    const root = document.getElementById('root');
    if (!active || !root) return undefined;
    root.inert = true;
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.add('identity-session-is-masked');
    return () => {
      root.inert = false;
      root.removeAttribute('aria-hidden');
      document.body.classList.remove('identity-session-is-masked');
    };
  }, [active]);
}

export default function SessionMask() {
  const { t } = useTranslation();
  const { masked, fullName, reverifyPin, reverifyFace, handover } = useIdentitySession();
  const [step, setStep] = useState('choice');
  const [pin, setPin] = useState('');
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [offerSomeoneElse, setOfferSomeoneElse] = useState(false);
  const [recognizing, setRecognizing] = useState(false);
  // Fora do efeito de reinício de propósito: a contagem sobrevive a um novo
  // mascaramento, como no protótipo, e só zera quando a identidade é confirmada.
  const [faceFailures, setFaceFailures] = useState(0);
  const continueRef = useRef(null);
  const pinRef = useRef(null);

  useInertBackground(masked);

  // Cada mascaramento começa do zero: nada do PIN digitado da última vez.
  useEffect(() => {
    if (!masked) return;
    setStep('choice');
    setPin('');
    setStatus(null);
    setBusy(false);
    setOfferSomeoneElse(false);
  }, [masked]);

  useEffect(() => {
    if (!masked) return;
    if (step === 'choice' && continueRef.current) continueRef.current.focus();
  }, [masked, step]);

  if (!masked) return null;

  const name = fullName || t('identity.mask.unknownDoctor');

  const confirmPin = async (event) => {
    event.preventDefault();
    if (!PIN_PATTERN.test(pin)) {
      setStatus({ tone: 'error', text: t('identity.mask.pinInvalid') });
      if (pinRef.current) pinRef.current.focus();
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      await reverifyPin(pin);
      setFaceFailures(0);
      setStatus({ tone: 'success', text: t('identity.mask.pinConfirmed') });
    } catch (error) {
      const code = identityErrorCode(error);
      const remaining = remainingAttempts(error);
      setPin('');
      if (code === 'pin_locked') {
        setStatus({ tone: 'error', text: t('identity.mask.pinLocked') });
      } else if (code === 'too_many_requests') {
        // Limite de requisições, não bloqueio. Antes caía no ramo de cima e
        // dizia "PIN bloqueado" a quem só insistiu na câmera.
        setStatus({ tone: 'error', text: t('identity.mask.tooManyRequests') });
      } else if (code === 'pin_refused') {
        setStatus({
          tone: 'error',
          text: remaining === null
            ? t('identity.mask.pinRefused')
            : t('identity.mask.pinRefusedRemaining', { count: remaining }),
        });
      } else if (code === 'session_gone') {
        // A sessão expirou enquanto a máscara estava de pé. O contexto leva
        // ao login na próxima leitura de estado; aqui só se diz o porquê.
        setStatus({ tone: 'error', text: t('identity.mask.sessionGone') });
      } else {
        setStatus({ tone: 'error', text: t('identity.mask.unavailable') });
      }
      if (pinRef.current) pinRef.current.focus();
    } finally {
      setBusy(false);
    }
  };

  const confirmFace = async (blob) => {
    setBusy(true);
    setRecognizing(true);
    setOfferSomeoneElse(false);
    setStatus({ tone: 'info', text: t('identity.mask.face.recognizing') });
    try {
      await reverifyFace(blob);
      setFaceFailures(0);
      setStatus({ tone: 'success', text: t('identity.mask.face.confirmed') });
    } catch (error) {
      const code = reverifyFaceFailureCode(error);
      setOfferSomeoneElse(code === 'different_person');
      const counts = COUNTED_FACE_FAILURES.has(code);
      const failures = counts ? faceFailures + 1 : faceFailures;
      if (counts) setFaceFailures(failures);
      const reason = t(FACE_FAILURE_KEY[code] || 'identity.mask.face.unavailable');
      const left = MAX_FACE_ATTEMPTS - failures;
      let text = reason;
      if (counts) {
        text = left > 0
          ? `${reason} ${t('identity.mask.face.attemptsLeft', { count: left })}`
          : t('identity.mask.face.locked', { count: MAX_FACE_ATTEMPTS });
      }
      setStatus({ tone: 'error', text });
    } finally {
      setBusy(false);
      setRecognizing(false);
    }
  };

  const becomeSomeoneElse = async () => {
    setBusy(true);
    try {
      await handover();
    } finally {
      // Sem o `finally`, uma rejeição aqui deixava `busy` em `true` para
      // sempre — e este diálogo, por desenho, não tem como fechar. O
      // resultado era uma tela sem saída nenhuma: nem "Continuar como", nem
      // "Sou outra pessoa", nem X. Exatamente o caso que o comentário no topo
      // deste arquivo diz temer.
      setBusy(false);
    }
  };

  return createPortal(
    <div className="identity-session-mask" id="identitySessionMask">
      <div className="identity-session-mask-surface" />
      <section
        className="identity-reconfirm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="identityReconfirmTitle"
        aria-describedby="identityReconfirmOwner"
      >
        <span className="material-symbols-rounded identity-reconfirm-icon" aria-hidden="true">
          shield_lock
        </span>
        <p className="identity-kicker">{t('identity.mask.kicker')}</p>
        <h2 id="identityReconfirmTitle">{t('identity.mask.title')}</h2>
        <p id="identityReconfirmOwner">
          <Trans i18nKey="identity.mask.owner" values={{ name }} components={{ strong: <strong /> }} />
        </p>

        {step === 'choice' ? (
          <div className="identity-reconfirm-choice">
            <button
              ref={continueRef}
              className="btn primary"
              type="button"
              onClick={() => setStep('verify')}
              disabled={busy}
            >
              <span className="material-symbols-rounded" aria-hidden="true">face</span>
              {t('identity.mask.continueAs', { name: firstNameOf(fullName) || name })}
            </button>
            <button className="btn outline" type="button" onClick={becomeSomeoneElse} disabled={busy}>
              <span className="material-symbols-rounded" aria-hidden="true">person_add</span>
              {t('identity.mask.someoneElse')}
            </button>
          </div>
        ) : (
          <ReconfirmVerify
            pin={pin}
            onPinChange={setPin}
            pinRef={pinRef}
            busy={busy}
            recognizing={recognizing}
            faceLocked={faceFailures >= MAX_FACE_ATTEMPTS}
            status={status}
            offerSomeoneElse={offerSomeoneElse}
            onSubmitPin={confirmPin}
            onFace={confirmFace}
            onCameraError={(text) => setStatus({ tone: 'error', text })}
            onSomeoneElse={becomeSomeoneElse}
            onBack={() => {
              setStatus(null);
              setOfferSomeoneElse(false);
              setStep('choice');
            }}
          />
        )}
      </section>
    </div>,
    document.body,
  );
}

const CAMERA_ERROR_KEY = {
  denied: 'identity.confirm.cameraErrors.blocked',
  no_camera: 'identity.confirm.cameraErrors.notFound',
  in_use: 'identity.confirm.cameraErrors.busy',
  lost: 'identity.confirm.cameraErrors.lost',
  unsupported: 'identity.confirm.cameraErrors.unsupported',
};

/**
 * O passo de prova: rosto ou PIN, como no protótipo. Componente próprio para
 * a câmera só ligar quando o médico escolhe "Continuar como" — e desligar
 * quando ele volta ou a máscara sai, que é quando este componente desmonta.
 */
function ReconfirmVerify({
  pin,
  onPinChange,
  pinRef,
  busy,
  recognizing,
  faceLocked,
  status,
  offerSomeoneElse,
  onSubmitPin,
  onFace,
  onCameraError,
  onSomeoneElse,
  onBack,
}) {
  const { t } = useTranslation();
  const camera = useFaceCapture();
  const { start: startCamera } = camera;

  // Com o rosto bloqueado a câmera nem liga: o único caminho é o PIN.
  useEffect(() => {
    if (!faceLocked) startCamera();
  }, [startCamera, faceLocked]);

  const { stop: stopCamera } = camera;
  useEffect(() => {
    if (faceLocked) stopCamera();
  }, [faceLocked, stopCamera]);

  // Teclado primeiro no PIN: quem não usa mouse chega na prova sem caçar o
  // campo, e o rosto continua a um clique.
  useEffect(() => {
    if (pinRef.current) pinRef.current.focus();
  }, [pinRef]);

  useEffect(() => {
    if (camera.status === CAPTURE_ERROR) {
      onCameraError(t(CAMERA_ERROR_KEY[camera.errorKind] || 'identity.mask.face.captureFailed'));
    }
    // Só a transição para erro interessa; o texto é derivado dela.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera.status, camera.errorKind]);

  const recognize = async () => {
    const blob = await camera.capture();
    if (!blob) return; // o próprio hook já foi para `error`, e o efeito acima diz o porquê
    await onFace(blob);
  };

  return (
    <form className="identity-reconfirm-verify" onSubmit={onSubmitPin} noValidate>
      {!faceLocked ? (
        <div className={`identity-camera-view${recognizing ? ' is-scanning' : ''}`} aria-hidden="true">
          <video ref={camera.videoRef} muted playsInline autoPlay className="identity-camera-video" />
          <span className="identity-camera-frame" />
          {recognizing ? <span className="identity-face-scan-line" /> : null}
          {!camera.isReady ? (
            <span className="material-symbols-rounded identity-camera-placeholder">
              {camera.status === CAPTURE_STARTING ? 'hourglass_top' : 'face'}
            </span>
          ) : null}
        </div>
      ) : null}
      <p
        className={`identity-reconfirm-status${status ? ` is-${status.tone}` : ''}`}
        role="status"
        aria-live="polite"
      >
        {status && status.tone === 'error' ? (
          <span className="material-symbols-rounded" aria-hidden="true">error</span>
        ) : null}
        <span>{status ? status.text : t(faceLocked ? 'identity.mask.face.lockedHint' : 'identity.mask.face.hint')}</span>
      </p>
      {offerSomeoneElse ? (
        <button className="btn outline identity-reconfirm-other" type="button" onClick={onSomeoneElse} disabled={busy}>
          <span className="material-symbols-rounded" aria-hidden="true">person_add</span>
          {t('identity.mask.someoneElse')}
        </button>
      ) : null}
      <div className="identity-reconfirm-methods">
        <button
          className="btn primary"
          type="button"
          onClick={recognize}
          disabled={busy || faceLocked || !camera.isReady}
        >
          {recognizing ? <span className="identity-btn-spinner" aria-hidden="true" /> : null}
          {t(recognizing ? 'identity.mask.face.recognizingButton' : 'identity.mask.face.recognize')}
        </button>
        <span className="identity-reconfirm-or">{t('identity.mask.face.or')}</span>
        <label className="identity-reconfirm-pin-field">
          <span className="direct-chat-sr-only">{t('identity.mask.pinLabel')}</span>
          <input
            ref={pinRef}
            className="identity-pin-input"
            id="identityReconfirmPin"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            maxLength={8}
            placeholder={t('identity.mask.pinPlaceholder')}
            value={pin}
            onChange={(event) => onPinChange(event.target.value.replace(/\D/g, ''))}
            disabled={busy}
          />
        </label>
        <button className="btn outline" type="submit" disabled={busy}>
          {t('identity.mask.confirmPin')}
        </button>
      </div>
      {IDENTITY_DEMO_ENABLED && !faceLocked ? <DemoFaceControls /> : null}
      <button className="btn text identity-reconfirm-back" type="button" onClick={onBack} disabled={busy}>
        {t('identity.mask.back')}
      </button>
    </form>
  );
}
